import { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import type { BulkImportAuthorityItem } from '@shared/types/api';
import { DownloadTemplateButton } from './download-template-button';

interface ExcelUploadModalProps {
  isOpen: boolean;
  isImporting: boolean;
  onClose: () => void;
  onImport: (items: BulkImportAuthorityItem[]) => Promise<void>;
}

export function ExcelUploadModal({
  isOpen,
  isImporting,
  onClose,
  onImport,
}: ExcelUploadModalProps) {
  const [parsedItems, setParsedItems] = useState<BulkImportAuthorityItem[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  // Reset internal state whenever modal opens or closes
  useEffect(() => {
    if (!isOpen) {
      setParsedItems([]);
      setUploadError(null);
      setFileName(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleClose = () => {
    setParsedItems([]);
    setUploadError(null);
    setFileName(null);
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUploadError(null);
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    const reader = new FileReader();

    reader.onload = (evt) => {
      try {
        const data = evt.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json<Record<string, any>>(sheet);

        if (!rows.length) {
          setUploadError('The uploaded sheet is empty.');
          setParsedItems([]);
          return;
        }

        const items: BulkImportAuthorityItem[] = [];

        for (let i = 0; i < rows.length; i++) {
          const row = rows[i];
          const authName =
            row['Authority Name'] || row['Authority'] || row['Ministry'] || row['authorityName'] || '';
          const authCode =
            row['Authority Code'] || row['Authority ID'] || row['authorityCode'] || '';
          const serviceId =
            row['Service ID'] || row['ID'] || row['Service Code'] || row['serviceId'] || '';
          const serviceName =
            row['Service Name'] || row['Service'] || row['Name'] || row['serviceName'] || '';

          if (authName.toString().trim() && serviceId.toString().trim() && serviceName.toString().trim()) {
            items.push({
              authorityName: authName.toString().trim(),
              authorityCode: authCode ? authCode.toString().trim() : undefined,
              serviceId: serviceId.toString().trim(),
              serviceName: serviceName.toString().trim(),
            });
          }
        }

        if (items.length === 0) {
          setUploadError(
            'No valid rows found. Please ensure columns include Authority Name, Service ID, and Service Name.',
          );
        }

        setParsedItems(items);
      } catch (err: any) {
        setUploadError('Failed to parse Excel file: ' + err.message);
        setParsedItems([]);
      }
    };

    reader.readAsBinaryString(file);
    // Reset file input element value so selecting the same file again triggers onChange
    e.target.value = '';
  };

  const handleUploadSubmit = async () => {
    if (!parsedItems.length) return;
    try {
      await onImport(parsedItems);
      handleClose();
    } catch (err: any) {
      setUploadError(err.response?.data?.message || err.message || 'Failed to import data');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Bulk Import Authorities via Excel</h3>
            <p className="text-xs text-gray-500">
              Upload an .xlsx, .xls or .csv file containing Authority Name, Service ID, and Service Name columns.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* File Dropzone */}
          <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50/50 p-6 text-center hover:border-emerald-500 transition-colors">
            <svg className="h-10 w-10 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.5"
                d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <label className="cursor-pointer font-medium text-emerald-600 hover:text-emerald-700">
              <span>Click to choose file</span>
              <input type="file" accept=".xlsx, .xls, .csv" className="sr-only" onChange={handleFileChange} />
            </label>
            <p className="text-xs text-gray-500 mt-1">Supports XLSX, XLS, CSV formats</p>
            {fileName && (
              <p className="mt-2 text-xs font-semibold text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full">
                {fileName}
              </p>
            )}
          </div>

          {/* Error display */}
          {uploadError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
              {uploadError}
            </div>
          )}

          {/* Parsed Preview Table */}
          {parsedItems.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Parsed Preview ({parsedItems.length} rows)
                </span>
                <span className="text-xs text-gray-500">Ready to import into database</span>
              </div>

              <div className="max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                <table className="w-full text-left text-xs">
                  <thead className="sticky top-0 bg-gray-50 text-gray-500 uppercase tracking-wider font-semibold border-b border-gray-200">
                    <tr>
                      <th className="py-2 px-3">#</th>
                      <th className="py-2 px-3">Authority Name</th>
                      <th className="py-2 px-3">Service ID</th>
                      <th className="py-2 px-3">Service Name</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 text-gray-700">
                    {parsedItems.map((item, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="py-2 px-3 text-gray-400">{idx + 1}</td>
                        <td className="py-2 px-3 font-medium text-gray-900">{item.authorityName}</td>
                        <td className="py-2 px-3 font-mono font-medium text-brand-700">{item.serviceId}</td>
                        <td className="py-2 px-3">{item.serviceName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-gray-200 px-6 py-4 bg-gray-50">
          <DownloadTemplateButton variant="link" />

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleUploadSubmit}
              disabled={!parsedItems.length || isImporting}
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
            >
              {isImporting ? 'Importing...' : `Import ${parsedItems.length} Items`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
